import type { AppliedFieldMapping } from "../../hooks/useAppliedFieldMapping";
import { FieldFormat, formatFieldValue, getNestedValue } from "../result-tab/config";

/** Map of `targetField → formatted sample value`. The mapping table uses it
 *  to fill the "Sample (row 1)" column with the first-row value for each
 *  mapped field. */
export type SampleValueLookup = Record<string, string>;

interface BuildSampleValueLookupOptions {
  /** Resolved entity that backs the first loaded record for the schema.
   *  When null/undefined (no load yet, or fetch in flight), every field
   *  resolves to a single dash so the column degrades quietly. */
  entity: Record<string, unknown> | null | undefined;
  /** Schema's applied mappings — used to identify which target fields to
   *  pull values for. */
  mappings: AppliedFieldMapping[];
  /** Optional formatter hint per `targetField`. Mirrors the
   *  `result-tab/config.ts` `FieldFormat` so currency / dates / claimant
   *  refs render in the same style they do in the Result tab. Fields not
   *  present in this map default to `"text"`. */
  formatByTargetField?: Record<string, FieldFormat>;
  /** Optional claimant id → name lookup, used when a mapped field has the
   *  `claimant_id_ref` format. */
  claimantLookup?: Record<string, string>;
}

/**
 * Build the sample-value lookup for a single schema's mapping table.
 *
 * For each mapping in the schema, reads the target field off the resolved
 * entity and formats it via the same `formatFieldValue` pipeline the Result
 * tab uses — so a `reimbursement_amount` shows as `$813.00` in both tabs
 * rather than diverging between `813` and `$813.00`.
 *
 * Returns `"—"` for any target field that isn't present (or is empty) on
 * the entity. Returns `"—"` for every field when the entity itself is null
 * (the consumer's "No sample available" fallback handles the wholly-empty
 * case at the column level — see SchemaMappingCard).
 */
export const buildSampleValueLookup = ({
  entity,
  mappings,
  formatByTargetField,
  claimantLookup,
}: BuildSampleValueLookupOptions): SampleValueLookup => {
  const lookup: SampleValueLookup = {};
  if (!entity) {
    // Populate every key with a dash so the consumer can still iterate the
    // mappings array without having to guard each cell.
    for (const mapping of mappings) {
      lookup[mapping.targetField] = "—";
    }
    return lookup;
  }

  for (const mapping of mappings) {
    const raw = getNestedValue(entity, mapping.targetField);
    const format = formatByTargetField?.[mapping.targetField] ?? "text";
    lookup[mapping.targetField] = formatFieldValue(raw, format, claimantLookup);
  }
  return lookup;
};
